"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader, CircleCheck, ShieldCheck, Clock, Zap } from "lucide-react";
function QuoteFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-secondary/20 rounded-lg flex items-center justify-center mb-6">
          <CircleCheck className="size-8 text-secondary" />
        </div>
        <h3 className="text-2xl font-bold font-default text-primary-foreground mb-2">
          {successMessage}
        </h3>
        <p className="text-primary-foreground/80 mb-8 max-w-md">
          We have received your request and will get back to you shortly with a customized quote.
        </p>
        <Button onClick={resetForm} variant="outline" className="bg-transparent border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/10">
          Submit Another Request
        </Button>
      </div>;
  }
  const inputClasses = "bg-white/5 border-white/20 text-primary-foreground placeholder:text-primary-foreground/50 focus-visible:ring-secondary focus-visible:border-secondary focus-visible:ring-offset-0 focus-visible:ring-2 h-12";
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Your name" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage className="text-secondary" />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[0-9-+() ]*$/,
          message: "Invalid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(613) 000-0000" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage className="text-secondary" />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary-foreground">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="you@example.com" className={inputClasses} {...field} />
            </FormControl>
            <FormMessage className="text-secondary" />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="serviceType" rules={{
        required: "Please select a service type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground">Service Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className={inputClasses}>
                    <SelectValue placeholder="Select a service..." />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="residential">Residential Junk Removal</SelectItem>
                  <SelectItem value="commercial">Commercial Clearance</SelectItem>
                  <SelectItem value="yard-waste">Yard Waste Removal</SelectItem>
                  <SelectItem value="dumpster">Dumpster Rental</SelectItem>
                  <SelectItem value="other">Other Services</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage className="text-secondary" />
            </FormItem>} />

        <FormField control={control} name="postalCode" rules={{
        required: "Postal code is required",
        minLength: {
          value: 3,
          message: "Please enter a valid postal code"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground">Postal Code</FormLabel>
              <FormControl>
                <Input placeholder="e.g. K1A 0B1" className={inputClasses} {...field} />
              </FormControl>
              <FormMessage className="text-secondary" />
            </FormItem>} />
      </div>

      <FormField control={control} name="details" rules={{
      required: "Please provide some details about your request",
      minLength: {
        value: 10,
        message: "Please provide a bit more detail (min 10 characters)"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary-foreground">Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us what you need help with..." className={`min-h-[100px] resize-y ${inputClasses}`} {...field} />
            </FormControl>
            <FormMessage className="text-secondary" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/20 border border-destructive/50 rounded-lg text-primary-foreground text-sm">
          {submitError}
        </div>}

      <div className="pt-4">
        <Button type="submit" variant="secondary" disabled={isSubmitting} className="w-full h-14 text-lg font-bold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 bg-secondary text-secondary-foreground hover:bg-secondary/90">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Sending Request...
            </> : 'Get My Free Quote'}
        </Button>
        <p className="mt-3 text-center text-sm text-primary-foreground/60 flex items-center justify-center gap-2">
          <Clock className="size-4" />
          We'll respond within 2 hours
        </p>
      </div>
    </Form>;
}
export default function RequestAQuote() {
  const trustPoints = [{
    icon: ShieldCheck,
    text: "No obligation"
  }, {
    icon: Zap,
    text: "Free estimate"
  }, {
    icon: CircleCheck,
    text: "Transparent pricing"
  }];
  return <section id="request-a-quote" className="relative bg-primary py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 opacity-15 bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/noise_texture_dark.png')] mix-blend-overlay pointer-events-none" aria-hidden="true" />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-[800px] mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block py-1 px-4 rounded-lg bg-secondary/10 text-secondary font-semibold text-sm mb-4 tracking-wider uppercase">
              Text Us Photos for Instant Quote
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default text-primary-foreground tracking-tight">
              Ready to clear the clutter?
            </h2>
            <p className="mt-4 text-lg text-primary-foreground/80 max-w-2xl mx-auto">
              Fill out the form below and our team will get back to you with a fast, no-obligation quote.
            </p>
            
            <div className="flex flex-wrap justify-center gap-6 mt-8">
              {trustPoints.map((point, i) => <div key={i} data-index={i} className="flex items-center gap-2 text-primary-foreground/90 text-sm font-medium">
                  <point.icon className="size-5 text-secondary" />
                  {point.text}
                </div>)}
            </div>
          </div>

          <div className="bg-black/10 backdrop-blur-sm border border-white/10 p-6 md:p-10 rounded-2xl shadow-2xl">
            <FormProvider formId="request-quote-main" sectionName="Request a Quote" defaultValues={{
            name: "",
            email: "",
            phone: "",
            serviceType: "",
            postalCode: "",
            details: ""
          }} submitText="Get My Free Quote" successMessage="Thank you! Your quote request has been received.">
              <QuoteFormContent />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}